import { Component, OnInit, ElementRef, ViewChild, ChangeDetectorRef, AfterViewInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { trigger, transition, style, animate } from '@angular/animations';
import { DataserviceService } from '../dataservice.service';
import { SalesFlowService } from './sales-flow.service';
import { v4 as uuidv4 } from 'uuid';
import { firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, filter, tap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';

declare const google: any;

type StepKey =
  'leadinfo' |
  'plans' |
  'carriers' |
  'address' |
  'pin' |
  'wallet' |
  'time' |
  'review';

@Component({
  selector: 'app-sales-flow',
  templateUrl: './sales-flow.component.html',
  styleUrls: ['./sales-flow.component.css'],
  animations: [
    trigger('slideAnimation', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate('0.5s ease-out', style({ transform: 'translateX(0)', opacity: 1 })),
      ]),
      transition(':leave', [animate('0.5s ease-in', style({ transform: 'translateX(-100%)', opacity: 0 }))]),
    ]),
  ],
})
export class SalesFlowComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('addressInput', { static: false }) addressInput!: ElementRef;

  userId: string | null = null;
  userCrmId: number | null = null;
  private readonly DEFAULT_USER_CRM_ID = 1;
  private readonly DEFAULT_USER_UUID = 'YOUR-DEFAULT-UUID-HERE';

  // Navigation
  currentStep: StepKey = 'leadinfo';
  stepHistory: StepKey[] = [];
  fullData: { [key: string]: any } = {};
  submitted = false;
  private partnerMetadataLoaded = false;

  // Progress
  progressPercentage: number = 0;
  isLoading: boolean = false;
  spinnerText: string = 'Loading...';

  // Lead Info (General Info)
  addressForm: FormGroup = new FormGroup({
    address: new FormControl('', [Validators.required]),
    city: new FormControl('', Validators.required),
    state: new FormControl('', Validators.required),
    zip: new FormControl('', [Validators.required, this.checkForNumber, this.isZipValid]),
    locationType: new FormControl('Residential', Validators.required),
    honeypot: new FormControl('')
  });
  eligibleAddresses: any[] = [];
  selectedAddressIndex: number | null = null;
  selectedAddressObj: any = null;
  selectedAddress: any = null;
  showAddressList = false;
  showForm = true;
  suggestions: any[] = [];
  showPreOrderIframe = false;
  preOrderUrl: any = null;
  preOrderModalOpen = false;
  existingServiceData: any = null;
  existingServiceMessage = '';

  // Plans
  plansList: any[] = [];
  selectedInternet: any = null;
  selectedPlan: any = null;
  selectedPlanIndex: number | null = null;
  selectedPhone: any = null;
  selectedBusinessPhone: any = null;
  selectedBusinessPhone2: any = null;
  selectedExtender: any = null;
  selectedStaticIp: any = null;
  selectedGateway: any = null;
  selectedBattery: any = null;
  ownEquipment: any = null;
  phoneNumberOption: 'new' | 'keep_cci' | 'port_other' | null = null;
  businessLine1Flag: 'new' | 'consolidated' | 'other' | null = null;
  businessLine2Flag: 'new' | 'consolidated' | 'other' | null = null;
  phoneTransferFlag: 'new' | 'consolidated' | 'other' | null = null;
  phoneUpsellOpen = false;
  phoneUpsellContext: 'home' | 'business' | null = null;
  stepDownOpen = false;
  voipPlusRemoveOpen = false;
  showPhone = false;
  hideButton = true;

  // Carriers
  carriersForm: FormGroup = new FormGroup({
    phone: new FormControl(null),
    email: new FormControl(null)
  });
  contactForm: FormGroup = new FormGroup({
    phone: new FormControl(null),
    email: new FormControl(null)
  });
  portingLines: any[] = [];
  forms: FormGroup[] = [];
  activeLineIndex = 0;
  modalOpen = false;
  showLoaButton = false;
  buttonText = 'Send';
  loaRequired = false;
  needsLoaOnly = false;
  hasDuplicatePortNumbers = false;
  allowedPortAreaCodes: string[] = [];
  private phoneSubs: any[] = [];

  // Address Check (Customer Info)
  customerInfoForm: FormGroup = new FormGroup({
    first: new FormControl(''),
    last: new FormControl(''),
    phone: new FormControl(''),
    email: new FormControl(''),
    'text-consent': new FormControl(false),
    'billingCheckbox': new FormControl(true),
    'billing-address': new FormControl(null),
    'billing-address2': new FormControl(''),
    'billing-city': new FormControl(null),
    'billing-state': new FormControl(null),
    'billing-zip': new FormControl(null),
    honeypot: new FormControl('')
  });
  isBusiness = false;
  sameBilling = true;
  isChecked = true;

  // PIN
  pinInput: string = '';
  mfaChannel: 'sms' | 'email' = 'sms';
  pinCount = 0;
  pinModalOpen = false;
  deliveryMessage = '';
  otpEmailCooldown = 0;
  otpSmsCooldown = 0;
  walletEmailCooldown = 0;
  walletSmsCooldown = 0;
  private otpCooldownTimers: Record<'email' | 'sms', any> = { email: null, sms: null };
  private walletCooldownTimers: Record<'email' | 'sms', any> = { email: null, sms: null };

  // Digital Wallet
  walletForm: FormGroup = new FormGroup({
    phone: new FormControl(''),
    email: new FormControl('')
  });
  paymentVerified = false;
  walletModalOpen = false;
  displayText = '';
  showVerifyButton = false;
  shouldSkipWallet = false;

  // Time Slots
  timeSlots: any[] = [];
  groupedTimeSlots: Record<string, any[]> = {};
  limitedDates: string[] = [];
  selectedTimeSlot: any = null;
  selectedDate: string | null = null;
  private availableDates = new Set<string>();

  // Review
  orderNumber: string = '';
  cartInfo: any = null;
  charges: any[] = [];
  discounts: any[] = [];
  offers: { label: string; amount: number | 'Included' }[] = [];
  totalCharges = 0;
  totalDiscounts = 0;
  finalPrice = 0;
  isModalOpen = false;
  isExistingFlow = false;

  // Common
  error: boolean = false;
  errorText: string = '';
  addressCheckModal = false;
  infoModalOpen = false;
  states: any[] = [];
  savedETag: string = '';
  caseId: string = '';
  cartId: string = '';

  constructor(
    private fb: FormBuilder,
    private data: DataserviceService,
    private salesFlow: SalesFlowService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute
  ) {
    this.states = this.data.states || [];
    this.initializeForms();
  }

  ngOnInit() {
    // Define uuidRegex at the top level of the subscribe callback
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    this.route.queryParams.subscribe(params => {

      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

      // Check for various URL parameter names
      const urlParam = params['user_crm_id'] ||
        params['userCrmId'] ||
        params['user-crm-id'] ||
        params['userId'] ||
        params['user_id'] ||
        params['user-id'];

      if (urlParam) {
        const isUuid = uuidRegex.test(urlParam);

        if (isUuid) {
          this.userId = urlParam;
          this.fullData['userId'] = this.userId;
        } else {
          const crmId = Number(urlParam);
          if (!isNaN(crmId)) {
            this.userCrmId = crmId;
            this.fullData['userCrmId'] = this.userCrmId;
          }
        }
      } else {
        return;
      }
    });

    this.clearOtpCooldown('email');
    this.clearOtpCooldown('sms');
    this.clearWalletCooldown('email');
    this.clearWalletCooldown('sms');
    this.updateProgress();
    this.fetchPartnerAccessMetadata();
    this.setupAddressAutocomplete();
  }

  ngAfterViewInit() {
    this.initPlaceAutocomplete();
  }

  ngOnDestroy() {
    this.phoneSubs.forEach(s => s?.unsubscribe?.());
  }

  // ==================== FORM INITIALIZATION ====================

  private initializeForms() {
    // Address Form
    this.addressForm = this.fb.group({
      address: ['', [Validators.required]],
      city: ['', Validators.required],
      state: ['', Validators.required],
      zip: ['', [Validators.required, this.checkForNumber, this.isZipValid]],
      existing: [false],
      locationType: ['Residential', Validators.required],
      honeypot: ['']
    });

    // Customer Info Form
    this.customerInfoForm = this.fb.group({
      first: ['', [Validators.required, this.checkForNonNumber]],
      last: ['', [Validators.required, this.checkForNonNumber]],
      phone: ['', [Validators.required, this.checkForNumber, Validators.minLength(10), this.checkForRepNum]],
      email: ['', [Validators.required, Validators.email]],
      'text-consent': [false],
      'billingCheckbox': [true],
      'billing-address': [null, Validators.required],
      'billing-address2': [''],
      'billing-city': [null, Validators.required],
      'billing-state': [null, Validators.required],
      'billing-zip': [null, Validators.required],
      honeypot: ['']
    });

    // Contact Form (for carriers)
    this.contactForm = this.fb.group({
      phone: [null, [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      email: [null, [Validators.email, Validators.required]]
    });

    // Wallet Form
    this.walletForm = this.fb.group({
      phone: ['', [Validators.required]],
      email: ['', [Validators.email]]
    });
  }

  // ==================== NAVIGATION ====================

  private setStep(next: StepKey) {
    this.stepHistory.push(this.currentStep);
    this.currentStep = next;
    this.updateProgress();

    switch (next) {
      case 'plans':
        this.ngOnInitPlans();
        break;
      case 'carriers':
        this.ngOnInitCarriers();
        break;
      case 'address':
        this.ngOnInitAddress();
        break;
      case 'wallet':
        this.ngOnInitWallet();
        break;
      case 'time':
        this.ngOnInitTime();
        break;
      case 'review':
        this.ngOnInitReview();
        break;
    }
  }

  goBack() {
    if (this.submitted) return;
    const prev = this.stepHistory.pop();
    if (prev) {
      this.currentStep = prev;
      this.updateProgress();
    }
  }

  updateProgress() {
    const stepMap: Record<StepKey, number> = {
      'leadinfo': 1,
      'plans': 2,
      'carriers': 3,
      'address': 4,
      'pin': 5,
      'wallet': 6,
      'time': 7,
      'review': 8
    };
    const totalSteps = 8;
    this.progressPercentage = (stepMap[this.currentStep] / totalSteps) * 100;
  }

  // ==================== STEP HANDLERS ====================

  handleLeadInfo(event: any) {
    this.fullData = { ...this.fullData, ...event };
    this.setStep('plans');
  }

  handlePlans(event: any) {
    this.fullData = { ...this.fullData, ...event };
    const type = this.fullData?.['info']?.type;
    const loaRequired = Boolean(
      this.fullData?.['responseData']?.conversion?.['communcations']?.[0]?.loaRequired ??
      this.fullData?.['conversionInfo']?.['loaRequired']
    );
    const goToPort = type === 'port_other' || (type === 'keep_cci' && loaRequired);
    this.setStep(goToPort ? 'carriers' : 'address');
  }

  handleCarriers(event: any) {
    this.fullData = { ...this.fullData, ...event };
    this.setStep('address');
  }

  handleAddress(event: any) {
    this.fullData = { ...this.fullData, ...event };
    if (this.fullData?.['pinFlag']) {
      if (this.shouldSkipWalletSetup()) {
        this.fullData['paymentVerified'] = true;
        this.setStep('time');
      } else {
        this.fullData?.['paymentVerified'] ? this.setStep('time') : this.setStep('wallet');
      }
    } else {
      this.setStep('pin');
    }
  }

  handlePin(event: any) {
    this.fullData = { ...this.fullData, ...event };
    if (this.shouldSkipWalletSetup()) {
      this.fullData['paymentVerified'] = true;
      this.setStep('time');
      return;
    }
    this.fullData?.['paymentVerified'] ? this.setStep('time') : this.setStep('wallet');
  }

  handleWallet(event: any) {
    this.fullData = { ...this.fullData, ...event };
    this.setStep('time');
  }

  handleTime(event: any) {
    this.fullData = { ...this.fullData, ...event };
    this.setStep('review');
  }

  handleReview(event: any) {
    if (event === 'timeUnavailable') {
      this.currentStep = 'time';
    } else {
      this.fullData['review'] = event;
      this.submitted = true;
    }
  }

  // ==================== ADDRESS INFO (GENERAL INFO) ====================

  private setupAddressAutocomplete() {
    this.addressForm.get('address')?.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(value => {
        const v = (value ?? '').toString().trim();
        if (v.length < 3) {
          this.suggestions = [];
          this.setLoadingState(false);
        }
        if (!v) {
          this.addressForm.patchValue({ city: '', state: '', zip: '' }, { emitEvent: false });
        }
      }),
      filter(value => value && value.length >= 3),
      switchMap(() => {
        this.setLoadingState(true);
        return this.salesFlow.locationLookup(this.addressForm.get('address')?.value);
      })
    ).subscribe((res: any) => {
      this.setLoadingState(false);
      if (res?.result === 'success' && Array.isArray(res.data) && res.data.length > 0) {
        this.suggestions = res.data;
      } else {
        this.suggestions = [];
        // this.handleError("No valid addresses found.", res?.message || '');
      }
    }, e => {
      this.setLoadingState(false);
      this.handleError("We're having a problem finding the address. Please reach out to support.", e);
    });
  }

  selectSuggestion(suggestion: any) {
    const value = typeof suggestion === 'string' ? suggestion : (suggestion?.address || '');
    if (!value) return;

    let city = suggestion?.city || '';
    let state = suggestion?.state || '';
    let zip = this.zip5(suggestion?.zip || '');

    if ((!city || !state || !zip) && typeof value === 'string') {
      const m = value.match(/,\s*([^,]+),\s*([A-Za-z]{2})\s*(\d{5}(?:-\d{4})?)/);
      if (m) {
        city = city || m[1].trim();
        state = state || m[2].toUpperCase();
        zip = zip || this.zip5(m[3]);
      }
    }

    this.addressForm.patchValue({ address: value, city, state, zip }, { emitEvent: false });
    this.addressForm.updateValueAndValidity({ emitEvent: false });
    this.suggestions = [];
  }

  submitAddress() {
    if (this.addressForm.invalid) {
      this.addressForm.markAllAsTouched();
      return;
    }

    this.setLoadingState(true, 'Finding address...');
    this.selectedAddressIndex = null;
    this.selectedAddressObj = null;
    this.eligibleAddresses = [];
    this.hideButton = true;

    const zip5 = this.zip5(this.addressForm.get('zip')?.value);
    const obj = {
      address: this.addressForm.get('address')?.value ||
        `${this.addressForm.get('address')?.value}, ${this.addressForm.get('city')?.value}, ${this.addressForm.get('state')?.value} ${zip5}, USA`
    };

    this.salesFlow.locationLookup(obj.address).subscribe((res: any) => {
      this.setLoadingState(false);
      if (res?.result === 'success' && Array.isArray(res.data) && res.data.length > 0) {
        this.eligibleAddresses = res.data;
        this.selectedAddressIndex = null;
        this.selectedAddressObj = null;
        this.hideButton = true;
        this.showForm = false;
        this.showAddressList = true;
      } else {
        this.handleError("We couldn't find a valid address. Try simplifying it (no unit/apartment #) or contact support.", res?.message || 'Invalid address response');
      }
    }, err => {
      this.setLoadingState(false);
      this.handleError("We're having a problem finding the address. Please reach out to support.", err);
    });
  }

  selectAddress(i: number, a: any) {
    this.selectedAddressIndex = i;
    this.applySelectedAddress(i, a);
  }

  private applySelectedAddress(i: number, a: any) {
    this.selectedAddressObj = {
      zip: a.zip,
      city: a.city,
      address1: `${a.streetNumber} ${a.street}`,
      address2: a.unit,
      state: a.state,
      street: a.street,
      streetNumber: a.streetNumber,
      endUserLocation: a.endUserLocation,
      addressId: a.addressId,
      unitId: a.endUserLocation,
      fullAddress: a.address,
      existing: this.addressForm.get('existing')?.value,
      salesChannel: 'DSR',
      platform_source: 'Web'
    };
    this.selectedAddress = a;
    this.hideButton = false;
  }

  confirmAddress() {
    if (this.selectedAddressIndex === null) return;
    this.existingServiceData = null;
    this.existingServiceMessage = '';
    this.setLoadingState(true, 'Confirming address...');

    const locationType = this.addressForm.get('locationType')?.value || 'Residential';
    this.selectedAddressObj.addressType = locationType === 'Business' ? 'Business' : 'Residential';
    this.selectedAddressObj.locationType = locationType;
    const partnerName = 'Scout';
    this.selectedAddressObj.partnerName = partnerName;

    this.salesFlow.serviceability(this.selectedAddressObj).subscribe((res: any) => {
      this.setLoadingState(false);
      if (res.result === 'preorder' && res.data?.redirectUrl) {
        this.preOrderUrl = res.data.redirectUrl;
        this.preOrderModalOpen = true;
      } else if (res.result === 'existingService' && res.data) {
        this.existingServiceData = res.data;
        this.existingServiceMessage = res.message || 'We found existing service at this address.';
        // Show modal - you'll need to implement this
      } else if (res.result === 'success' && res.data) {
        this.emitServiceabilityResult(res.data);
      } else {
        this.handleError(res.message ? res.message : "Address confirmed, but no service is available or response was invalid.", res.message || res.data);
      }
    }, err => {
      this.setLoadingState(false);
      this.handleError("We're having a problem with confirming the address. Please reach out to support.", err);
    });
  }

  private emitServiceabilityResult(responseData: any, extras: any = {}) {
    this.handleLeadInfo({
      responseData,
      addressInfo: this.selectedAddressObj,
      addressType: this.addressForm.get('locationType')?.value,
      existing: this.addressForm.get('existing')?.value,
      ...extras
    });
  }

  // ==================== PLANS ====================

  ngOnInitPlans() {
    this.plansList = this.fullData?.['responseData']?.catalog || [];
    if (this.plansList.length > 0) {
      this.plansList.map((p: any) => {
        p.internet.productPriceCalculated = this.calculatePrice(p.internet);
      });
    }

    // Restore selected plan if it exists
    if (this.selectedInternet && this.plansList.length > 0) {
      // Try to find the plan in the list
      const foundIndex = this.plansList.findIndex(plan => {
        // Match by product name and price, or by object reference
        return plan === this.selectedInternet ||
          (plan.internet?.productName === this.selectedInternet?.internet?.productName &&
            plan.internet?.productPrice === this.selectedInternet?.internet?.productPrice);
      });

      if (foundIndex !== -1) {
        this.selectedPlanIndex = foundIndex;
        // Re-apply the selected plan to ensure all state is correct
        this.setCheckBoxValInternet(foundIndex);
      } else {
        // Plan not found, clear selection
        this.selectedInternet = null;
        this.selectedPlanIndex = null;
      }
    } else if (!this.selectedInternet) {
      // No plan was selected, ensure index is null
      this.selectedPlanIndex = null;
    }

    if (this.fullData?.['selectedInfo']) {
      // Restore selections
      if (this.fullData?.['selectedInfo']?.ownEquipment) this.setCheckBoxValOwn({ detail: { checked: true } });
      if (this.fullData?.['selectedInfo']?.battery) this.setCheckBoxValBattery({ detail: { checked: true, value: this.fullData?.['selectedInfo']?.battery } });
      if (this.fullData?.['selectedInfo']?.extender) this.setCheckBoxValExtender({ detail: { checked: true, value: this.fullData?.['selectedInfo']?.extender } });
      if (this.fullData?.['selectedInfo']?.staticIp) this.setCheckBoxValStaticIp({ detail: { checked: true, value: this.fullData?.['selectedInfo']?.staticIp } });
      if (this.fullData?.['selectedInfo']?.gateway) this.setCheckBoxValGateway({ detail: { checked: true, value: this.fullData?.['selectedInfo']?.gateway } });
      if (this.fullData?.['selectedInfo']?.phonePlan) this.togglePhoneSelection(true);
      if (this.fullData?.['selectedInfo']?.businessPhone) this.toggleBusinessPhoneSelection(true, 1);
      if (this.fullData?.['selectedInfo']?.businessPhone2) this.toggleBusinessPhoneSelection(true, 2);

      if (this.fullData?.['selectedInfo']?.phonePlanOption) {
        this.phoneNumberOption = this.fullData?.['selectedInfo']?.phonePlanOption;
      }
      if (this.fullData?.['selectedInfo']?.businessPhoneOption) {
        this.businessLine1Flag = this.mapTypeToBizFlag(this.fullData?.['selectedInfo']?.businessPhoneOption);
      }
      if (this.fullData?.['selectedInfo']?.businessPhone2Option) {
        this.businessLine2Flag = this.mapTypeToBizFlag(this.fullData?.['selectedInfo']?.businessPhone2Option);
      }
    }

    this.updateHideButton();
  }

  calculatePrice(p: any) {
    if (!p) return 0;
    const promos = Array.isArray(p.promotions) ? p.promotions : [];
    const skipAtPlanLevel = new Set<string>(['WXW']);
    let nonTerm = 0;
    const termAmts: number[] = [];

    promos.forEach((promotion: any) => {
      const classes = Array.isArray(promotion?.Classes) ? promotion.Classes : [];
      if (classes[0]?.Class === 'DSR BackPocket') return;
      const legacyId = String(promotion?.promotionLegacyId || promotion?.PromotionLegacyId || promotion?.LegacyID || '').toUpperCase();
      const name = String(promotion?.promotionName || promotion?.Name || promotion?.description || '').toLowerCase();
      const type = String(promotion?.promotionType || promotion?.Type || '').toLowerCase();
      if (type.includes('gift card') || name.includes('gift card')) return;
      if (skipAtPlanLevel.has(legacyId)) return;
      const isAutoApplied = !!(promotion?.isAutoApplied === true || promotion?.isAutoApplied === 'true' || promotion?.isAutoApplied === 1);
      const requiresOptIn = name.includes('autopay') || name.includes('auto pay') || name.includes('paperless');
      const forceInclude = legacyId === 'AUTOP' || legacyId === 'PLBIL';
      if (requiresOptIn && !isAutoApplied && !forceInclude) return;

      const term = promotion?.promotionTerms?.[0]?.DiscountAmount || promotion?.Term?.[0]?.DiscountAmount;
      if (term != null && term !== '') {
        const n = parseFloat(term);
        if (!isNaN(n)) termAmts.push(n);
        return;
      }

      const flat = promotion?.DiscountAmount ?? promotion?.discountAmount;
      if (flat != null && flat !== '') {
        const n = parseFloat(flat);
        if (!isNaN(n) && n > 0) nonTerm += n;
      }
    });

    const term = termAmts.length ? Math.min(...termAmts) : 0;
    const base = parseFloat(p.productPrice ?? p.price ?? 0) || 0;
    return Math.max(base - (nonTerm + term), 0);
  }

  setCheckBoxValInternet(indexOrPlan: any) {
    // Handle both index (from select) and direct plan object (for compatibility)
    let p: any;
    let idx: number | null = null;

    if (typeof indexOrPlan === 'number' || (typeof indexOrPlan === 'string' && indexOrPlan !== '' && !isNaN(Number(indexOrPlan)))) {
      idx = Number(indexOrPlan);
      p = this.plansList[idx];
    } else {
      p = indexOrPlan;
      // Find the index if we have the plan object
      if (p && this.plansList.length > 0) {
        idx = this.plansList.findIndex(plan => plan === p ||
          (plan.internet?.productName === p.internet?.productName &&
            plan.internet?.productPrice === p.internet?.productPrice));
      }
    }

    // If no plan selected (empty string or null), clear selection
    if (!p) {
      this.selectedInternet = null;
      this.selectedPlanIndex = null;
      this.selectedBattery = null;
      this.selectedExtender = null;
      this.selectedStaticIp = null;
      this.selectedGateway = null;
      this.ownEquipment = null;
      this.selectedPhone = null;
      this.selectedBusinessPhone = null;
      this.selectedBusinessPhone2 = null;
      this.phoneTransferFlag = null;
      this.businessLine1Flag = null;
      this.businessLine2Flag = null;
      this.phoneNumberOption = null;
      this.updateHideButton();
      return;
    }

    this.selectedInternet = p;
    this.selectedPlanIndex = idx;
    this.selectedInternet.internet.bclData = p.broadbandLabel;
    this.selectedBattery = null;
    this.selectedExtender = null;
    this.selectedStaticIp = null;
    this.selectedGateway = null;
    this.ownEquipment = null;
    this.selectedPhone = null;
    this.selectedBusinessPhone = null;
    this.selectedBusinessPhone2 = null;
    this.phoneTransferFlag = null;
    this.businessLine1Flag = null;
    this.businessLine2Flag = null;

    if (p?.monitoredBatteryBackup?.relationship === 'Optional Auto Include') this.selectedBattery = p.monitoredBatteryBackup;
    if (p?.wifiExtenders?.relationship === 'Optional Auto Include') {
      this.selectedExtender = p.wifiExtenders;
    }
    if (p?.staticIpAddress?.relationship === 'Optional Auto Include') {
      this.selectedStaticIp = p.staticIpAddress;
    }
    if (p?.wifiGateway?.relationship === 'Optional Auto Include') {
      this.selectedGateway = p.wifiGateway;
    }
    if (p?.useMyOwnEquipment?.relationship === 'Optional Auto Include') {
      this.ownEquipment = p.useMyOwnEquipment;
      this.selectedGateway = null;
    }

    const businessLine = p?.businessVoipUnlimited;
    if (businessLine && this.calculatePriceGeneric(businessLine).isIncluded) {
      this.selectedBusinessPhone = businessLine;
      this.ensureBusinessLineFlagDefault(1);
    }

    this.updateHideButton();
  }

  calculatePriceGeneric(product: any) {
    if (!product) return { finalPrice: 0, originalPrice: 0, isIncluded: false };
    const originalPrice = parseFloat(product.productPrice ?? product.price ?? 0) || 0;
    const promotions = Array.isArray(product.promotions) ? product.promotions : [];
    const discountAmount = promotions.reduce((total: number, promotion: any) => {
      const promoNameRaw = promotion?.promotionName || promotion?.Name || promotion?.description || '';
      const promoName = String(promoNameRaw).toLowerCase();
      const rawAutoApplied = promotion?.isAutoApplied;
      const isAutoApplied = rawAutoApplied === true || rawAutoApplied === 1 || rawAutoApplied === '1' || rawAutoApplied === 'true';
      const requiresOptIn = promoName.includes('autopay') || promoName.includes('auto pay') || promoName.includes('paperless');
      const forceInclude = this.shouldForceIncludeOptInPromotion(promotion);
      if (requiresOptIn && !isAutoApplied && !forceInclude) return total;
      if (promotion?.DiscountAmount) return total + parseFloat(promotion.DiscountAmount);
      if (promotion?.Term?.length > 0 && promotion.Term[0]?.DiscountAmount) {
        return total + parseFloat(promotion.Term[0].DiscountAmount);
      }
      return total;
    }, 0);
    const finalPrice = Math.max(originalPrice - discountAmount, 0);
    const isIncluded = finalPrice === 0 && originalPrice > 0;
    return { finalPrice, originalPrice, isIncluded };
  }

  private shouldForceIncludeOptInPromotion(promotion: any): boolean {
    if (!promotion) return false;
    const legacyId = String(promotion?.promotionLegacyId || promotion?.PromotionLegacyId || '').toUpperCase();
    if (legacyId === 'AUTOP' || legacyId === 'PLBIL') return true;
    const promoNameRaw = promotion?.promotionName || promotion?.Name || promotion?.description || '';
    const promoName = String(promoNameRaw).toLowerCase();
    return promoName.includes('autopay') || promoName.includes('auto pay') || promoName.includes('paperless');
  }

  togglePhoneSelection(checked: boolean) {
    if (!checked) {
      this.selectedPhone = null;
      this.phoneNumberOption = null;
      this.updateHideButton();
      return;
    }
    const plus = this.selectedInternet?.homePhonePlus || null;
    const base = this.selectedInternet?.homePhone || null;
    this.selectedPhone = plus || base || null;
    this.phoneNumberOption = null;
    this.updateHideButton();
  }

  setCheckBoxValBattery(event: any) {
    const checked = event.target?.checked ?? event.detail?.checked ?? false;
    if (checked) {
      this.selectedBattery = this.selectedInternet?.monitoredBatteryBackup || event.detail?.value || event.target?.value;
    } else {
      this.selectedBattery = null;
    }
    this.updateHideButton();
  }

  setCheckBoxValOwn(event: any) {
    const checked = event.target?.checked ?? event.detail?.checked ?? false;
    if (checked) {
      this.ownEquipment = this.selectedInternet?.useMyOwnEquipment || event.detail?.value || event.target?.value;
      this.selectedGateway = null;
      this.selectedExtender = null;
      this.selectedStaticIp = null;
    } else {
      this.ownEquipment = null;
    }
    this.updateHideButton();
  }

  setCheckBoxValExtender(event: any) {
    const checked = event.target?.checked ?? event.detail?.checked ?? false;
    if (checked) {
      this.selectedExtender = this.selectedInternet?.wifiExtenders || event.detail?.value || event.target?.value;
    } else {
      this.selectedExtender = null;
    }
    this.updateHideButton();
  }

  setCheckBoxValStaticIp(event: any) {
    const checked = event.target?.checked ?? event.detail?.checked ?? false;
    if (checked) {
      this.selectedStaticIp = this.selectedInternet?.staticIpAddress || event.detail?.value || event.target?.value;
    } else {
      this.selectedStaticIp = null;
    }
    this.updateHideButton();
  }

  setCheckBoxValGateway(event: any) {
    if (this.ownEquipment) {
      this.selectedGateway = null;
      this.updateHideButton();
      return;
    }
    const checked = event.target?.checked ?? event.detail?.checked ?? false;
    if (checked) {
      this.selectedGateway = this.selectedInternet?.wifiGateway || event.detail?.value || event.target?.value;
      // Don't clear ownEquipment here - the HTML disables gateway when ownEquipment is selected
    } else {
      this.selectedGateway = null;
    }
    this.updateHideButton();
  }

  toggleBusinessPhoneSelection(checked: boolean, x: number) {
    if (checked) {
      if (x === 1) {
        this.selectedBusinessPhone = this.selectedInternet?.businessVoipUnlimited || null;
        this.ensureBusinessLineFlagDefault(1);
      } else {
        this.selectedBusinessPhone2 = this.selectedInternet?.businessVoipUnlimited || null;
        this.ensureBusinessLineFlagDefault(2);
      }
    } else {
      if (x === 1) {
        this.selectedBusinessPhone = null;
        this.businessLine1Flag = null;
      } else {
        this.selectedBusinessPhone2 = null;
        this.businessLine2Flag = null;
      }
    }
    this.updateHideButton();
  }

  private ensureBusinessLineFlagDefault(line: 1 | 2): void {
    if (line === 1 && !this.businessLine1Flag) {
      this.businessLine1Flag = 'new';
    }
    if (line === 2 && !this.businessLine2Flag) {
      this.businessLine2Flag = 'new';
    }
  }

  private mapTypeToBizFlag(type: 'new' | 'keep_cci' | 'port_other' | null): 'new' | 'consolidated' | 'other' | null {
    if (type === 'keep_cci') return 'consolidated';
    if (type === 'port_other') return 'other';
    if (type === 'new') return 'new';
    return null;
  }

  private mapBizFlagToType(flag: 'new' | 'consolidated' | 'other' | null): 'new' | 'keep_cci' | 'port_other' {
    if (flag === 'other') return 'port_other';
    if (flag === 'consolidated') return 'keep_cci';
    return 'new';
  }

  private deriveType(): 'new' | 'keep_cci' | 'port_other' | null {
    const types: Array<'new' | 'keep_cci' | 'port_other'> = [];
    if (this.selectedPhone && this.phoneNumberOption) types.push(this.phoneNumberOption);
    if (this.selectedBusinessPhone) types.push(this.mapBizFlagToType(this.businessLine1Flag));
    if (this.selectedBusinessPhone2) types.push(this.mapBizFlagToType(this.businessLine2Flag));
    if (!types.length) return 'new';
    if (types.includes('port_other')) return 'port_other';
    if (types.includes('keep_cci')) return 'keep_cci';
    return 'new';
  }

  private updateHideButton() {
    const needHome = !!this.selectedPhone && !this.phoneNumberOption;
    const needBL1 = !!this.selectedBusinessPhone && !this.businessLine1Flag;
    const needBL2 = !!this.selectedBusinessPhone2 && !this.businessLine2Flag;
    const hasEquipmentOptions = !!(this.selectedInternet?.wifiGateway || this.selectedInternet?.useMyOwnEquipment);
    const needEquipmentChoice = hasEquipmentOptions && !this.isEquipmentValid;
    this.hideButton = !this.selectedInternet || needHome || needBL1 || needBL2 || needEquipmentChoice;
  }

  get isEquipmentValid(): boolean {
    return !!(this.ownEquipment || this.selectedGateway);
  }

  submitPlans() {
    if (!this.selectedInternet) {
      this.handleError('Please select a plan before continuing.', 'Plans');
      return;
    }
    this.isLoading = true;
    this.spinnerText = 'Updating your cart...';

    const selectedInfo: any = {
      extender: this.selectedExtender || null,
      staticIp: this.selectedStaticIp || null,
      gateway: this.selectedGateway || null,
      battery: this.selectedBattery || null,
      ownEquipment: this.ownEquipment || null,
      internetPlan: this.selectedInternet ? this.selectedInternet.internet : null,
      phonePlan: this.selectedPhone || null,
      businessPhone: this.selectedBusinessPhone || null,
      businessPhone2: this.selectedBusinessPhone2 || null
    };

    selectedInfo.phonePlanOption = this.selectedPhone ? (this.phoneNumberOption || 'new') : null;
    selectedInfo.businessPhoneOption = this.selectedBusinessPhone ? this.mapBizFlagToType(this.businessLine1Flag) : null;
    selectedInfo.businessPhone2Option = this.selectedBusinessPhone2 ? this.mapBizFlagToType(this.businessLine2Flag) : null;

    const partnerName = 'Scout';
    this.fullData['partnerName'] = partnerName;

    const obj = {
      info: this.fullData,
      selectedInfo,
      'fidium-id': '',
      cartId: this.fullData?.['responseData']?.createCart?.cartId,
      partnerName
    };

    this.fullData['savedAddonPayloads'] = this.buildSavedAddonPayloads(selectedInfo, obj.cartId);
    obj.info['type'] = this.deriveType();

    const loaRequired = !!(
      this.fullData?.['conversionInfo']?.['loaRequired'] ??
      this.fullData?.['responseData']?.['conversion']?.['communications']?.[0]?.['loaRequired']
    );

    const homeOpt = selectedInfo.phonePlanOption;
    const bl1Opt = selectedInfo.businessPhoneOption;
    const bl2Opt = selectedInfo.businessPhone2Option;
    const isPortOther = (v: any) => v === 'port_other';
    const isKeepCci = (v: any) => v === 'keep_cci';
    const homeKeepWithNumber = !!(isKeepCci(homeOpt) && loaRequired);
    obj.info['existingNumber'] = homeKeepWithNumber || isPortOther(homeOpt);
    obj.info['existingNumber2'] = isPortOther(bl1Opt) || isPortOther(bl2Opt) || (loaRequired && (isKeepCci(bl1Opt) || isKeepCci(bl2Opt)));

    this.salesFlow.updateCartSelection(obj).subscribe({
      next: (r: any) => {
        this.isLoading = false;
        if (r.result === 'success') {
          this.handlePlans(obj);
        } else {
          this.handleError('Unable to submit plans. Please reach out to support.', 'Plans');
        }
      },
      error: e => {
        this.isLoading = false;
        this.handleError('Unable to submit plans. Please reach out to support.', 'Plans');
      }
    });
  }

  private buildSavedAddonPayloads(selectedInfo: any, cartId: string): any[] {
    if (!cartId) return [];
    const payloads: any[] = [];
    const pushPayload = (addon: any, lineKey: 'phonePlan' | 'businessPhone' | 'businessPhone2') => {
      if (!addon?.productID) return;
      const payload: any = {
        cartId,
        addonId: addon.productID,
        quantity: '1',
        metaLineKey: lineKey
      };
      if (addon.phoneTransferDetails) {
        payload.phoneTransferDetails = addon.phoneTransferDetails;
      }
      payloads.push(payload);
    };

    pushPayload(selectedInfo.phonePlan, 'phonePlan');
    pushPayload(selectedInfo.businessPhone, 'businessPhone');
    pushPayload(selectedInfo.businessPhone2, 'businessPhone2');

    return payloads;
  }

  // ==================== CARRIERS ====================

  ngOnInitCarriers() {
    this.contactForm = this.fb.group({
      phone: [null, [
        this.forbiddenContactValidator(''),
        Validators.required,
        Validators.pattern(/^[0-9]{10}$/)
      ]],
      email: [null, [
        Validators.email,
        this.forbiddenContactValidator(''),
        Validators.required
      ]]
    });

    const sel = this.fullData?.['selectedInfo'] || {};
    if (sel.phonePlan && sel.phonePlanOption === 'port_other') {
      this.portingLines.push({ label: 'Carriers', key: 'phonePlan' });
    }
    if (sel.businessPhone && sel.businessPhoneOption === 'port_other') {
      this.portingLines.push({ label: 'Business Line 1', key: 'businessPhone' });
    }
    if (sel.businessPhone2 && sel.businessPhone2Option === 'port_other') {
      this.portingLines.push({ label: 'Business Line 2', key: 'businessPhone2' });
    }

    const type = this.fullData?.['info']?.type;
    const loaRequired = Boolean(
      this.fullData?.['responseData']?.['conversion']?.['communcations']?.[0]?.['loaRequired'] ??
      this.fullData?.['conversionInfo']?.['loaRequired']
    );
    this.loaRequired = loaRequired;
    this.needsLoaOnly = type === 'keep_cci' && loaRequired && this.portingLines.length === 0;

    const region = this.fullData?.['responseData']?.region || this.fullData?.['responseData']?.createCart?.region;
    if (region === 'NNE') {
      this.allowedPortAreaCodes = ['207', '802', '603'];
    }

    this.forms = this.portingLines.map((_, i) => this.createLineForm(i));

    this.forms.forEach((fg) => {
      const ctrl = fg.get('phonePortNum');
      const sub = ctrl?.valueChanges.subscribe(() => this.updateDuplicateNumberErrors());
      if (sub) this.phoneSubs.push(sub);
    });
    this.updateDuplicateNumberErrors();
  }

  forbiddenContactValidator = (forbiddenValue: string): any => {
    return (control: AbstractControl) => {
      return control.value === forbiddenValue ? { forbiddenContact: true } : null;
    };
  };

  createLineForm(index: number): FormGroup {
    const portValidators: any[] = [Validators.required, Validators.pattern(/^[0-9]{10}$/)];
    if (this.allowedPortAreaCodes.length) {
      portValidators.push(this.areaCodeValidator(this.allowedPortAreaCodes));
    }

    const initialPhoneValue = null;

    return this.fb.group({
      phonePortNum: [initialPhoneValue, portValidators],
      accountNum: [null, [Validators.required, Validators.pattern(/^[0-9]+$/)]],
      accountName: [null, Validators.required],
      callerFirst: [null, Validators.required],
      callerLast: [null, [Validators.required, Validators.maxLength(13)]],
      address: [this.fullData?.['addressInfo']?.['address1'], Validators.required],
      address2: [this.fullData?.['addressInfo']?.['address2'] ?? null],
      city: [this.fullData?.['addressInfo']?.['city'], Validators.required],
      state: [this.fullData?.['addressInfo']?.['state'], Validators.required],
      zip: [this.fullData?.['addressInfo']?.['zip'], Validators.required],
      transferPin: [null]
    });
  }

  areaCodeValidator = (allowed: string[]): any => {
    return (control: AbstractControl) => {
      const value = control.value;
      if (!value) return null;
      const digits = String(value).replace(/\D/g, '');
      if (digits.length < 3) return null;
      const ac = digits.substring(0, 3);
      return allowed.includes(ac) ? null : { areaCodeNotAllowed: true };
    };
  };

  private updateDuplicateNumberErrors(): void {
    const values = this.forms.map(f => this.sanitizePhone(f.get('phonePortNum')?.value));
    const counts = new Map<string, number>();
    values.forEach(v => {
      if (v && v.length === 10) counts.set(v, (counts.get(v) || 0) + 1);
    });
    let hasDup = false;
    this.forms.forEach((f, idx) => {
      const ctrl = f.get('phonePortNum');
      if (!ctrl) return;
      const v = values[idx];
      const isDup = v && v.length === 10 && (counts.get(v) || 0) > 1;
      const errors = ctrl.errors || {};
      if (isDup) {
        hasDup = true;
        errors['duplicatePortNumber'] = true;
        ctrl.setErrors(errors);
      } else if (errors['duplicatePortNumber']) {
        delete errors['duplicatePortNumber'];
        const keys = Object.keys(errors);
        ctrl.setErrors(keys.length ? errors : null);
      }
    });
    this.hasDuplicatePortNumbers = hasDup;
  }

  private sanitizePhone(v: any): string {
    return String(v ?? '').replace(/\D/g, '');
  }

  submitPort(i: number) {
    this.activeLineIndex = i;
    const indices = this.forms
      .map((f, idx) => ({ idx, val: f.get('phonePortNum')?.value }))
      .filter(x => !!x.val)
      .map(x => x.idx);

    if (!indices.length) {
      this.modalOpen = true;
      return;
    }

    this.isLoading = true;
    this.spinnerText = 'Checking phone number(s)...';
    let pendingLookups = indices.length;

    indices.forEach((x) => {
      const phone = this.forms[x].get('phonePortNum')?.value;
      this.salesFlow.phoneLookup({ phone, data: this.fullData }).subscribe((r: any) => {
        if (r.data.status.toLowerCase() === 'success') {
          this.fullData['phoneLookupRes'] = r.data.data;
          pendingLookups--;
          if (pendingLookups === 0) {
            this.patchCart();
          }
        } else {
          this.isLoading = false;
          this.handleError('Please try another number.', 'Phone Lookup');
        }
      }, () => {
        this.isLoading = false;
        this.handleError('Please try another number.', 'Phone Lookup');
      });
    });
  }

  patchCart() {
    const indices = this.forms
      .map((f, idx) => ({ idx, val: f.get('phonePortNum')?.value }))
      .filter(x => !!x.val)
      .map(x => x.idx);

    if (!indices.length) {
      this.isLoading = false;
      this.modalOpen = true;
      return;
    }

    indices.forEach((x) => {
      const lineKey = this.portingLines?.[x]?.key;
      const addon = this.resolveAddonByKey(lineKey);
      if (!addon?.productID) return;

      const payload = {
        "cartId": this.fullData?.['responseData']?.['createCart']?.['cartId'],
        "addonId": addon.productID,
        "quantity": '1',
        'phoneTransferDetails': {
          "assignNewNumber": false,
          "moveExistingNumber": false,
          "existingNumber": this.forms[x].get('phonePortNum')?.value,
          "existingCarrier": this.fullData?.['phoneLookupRes']?.['carrierName'],
          "accountNumber": this.forms[x].get('accountNum')?.value,
          "accountName": this.forms[x].get('accountName')?.value,
          "address1": this.forms[x].get('address')?.value,
          "address2": this.forms[x].get('address2')?.value,
          "city": this.forms[x].get('city')?.value,
          "state": this.forms[x].get('state')?.value,
          "zip": this.forms[x].get('zip')?.value,
          "transferPin": this.forms[x].get('transferPin')?.value,
          "internationalBlock": false,
          "callerIdDisplay": String(this.forms[x].get('callerLast')?.value || '').slice(0, 13) + ',' + String(this.forms[x].get('callerFirst')?.value || '').charAt(0)
        }
      };

      this.updateSavedAddonPayload(lineKey, payload);
    });

    if (this.loaRequired) {
      this.isLoading = false;
      this.modalOpen = true;
      return;
    }

    const savedPayloads = this.getSavedAddonPayloads();
    if (!savedPayloads.length) {
      this.isLoading = false;
      this.modalOpen = true;
      return;
    }

    this.isLoading = true;
    this.spinnerText = 'Updating phone details...';
    this.applyAddonPayloads(savedPayloads)
      .then(() => {
        this.isLoading = false;
        this.modalOpen = true;
      })
      .catch(() => {
        this.isLoading = false;
        this.handleError('Could not update phone details. Please try again.', 'Phone Details');
      });
  }

  private resolveAddonByKey(key: string | undefined): any {
    if (!key) return null;
    const sel = this.fullData?.['selectedInfo'] || {};
    if (key === 'phonePlan') return sel.phonePlan;
    if (key === 'businessPhone') return sel.businessPhone;
    if (key === 'businessPhone2') return sel.businessPhone2;
    return null;
  }

  private getSavedAddonPayloads(): any[] {
    return Array.isArray(this.fullData?.['savedAddonPayloads']) ? [...this.fullData?.['savedAddonPayloads']] : [];
  }

  private updateSavedAddonPayload(lineKey: string | undefined, payload: any): void {
    if (!lineKey || !payload) return;
    const saved = this.getSavedAddonPayloads();
    const next = { ...payload, metaLineKey: lineKey };
    const idx = saved.findIndex((p: any) => p?.metaLineKey === lineKey);
    if (idx >= 0) saved[idx] = next;
    else saved.push(next);
    this.fullData['savedAddonPayloads'] = saved;
  }

  private async applyAddonPayloads(payloads: any[]): Promise<void> {
    if (!payloads.length) return;
    const removePayload = this.buildRemovePayload();
    await firstValueFrom(this.salesFlow.removeAddons(removePayload));
    for (const payload of payloads) {
      const body = { ...payload };
      delete body.metaLineKey;
      await firstValueFrom(this.salesFlow.updateAddons(body));
    }
  }

  private buildRemovePayload(): any {
    const payload: any = {
      notSelected: {},
      info: {
        responseData: {
          createCart: {
            cartId: this.fullData?.['responseData']?.['createCart']?.['cartId']
          }
        }
      }
    };

    if (this.fullData?.['selectedInfo']?.phonePlan) payload.notSelected.phonePlan = this.fullData?.['selectedInfo']?.phonePlan;
    if (this.fullData?.['selectedInfo']?.businessPhone) payload.notSelected.businessPhone = this.fullData?.['selectedInfo']?.businessPhone;
    if (this.fullData?.['selectedInfo']?.businessPhone2) payload.notSelected.businessPhone2 = this.fullData?.['selectedInfo']?.businessPhone2;

    return payload;
  }

  sendLoaMessage(x: string) {
    const obj: any = {
      type: x,
      cartId: this.fullData?.['info']?.['responseData']?.['createCart']?.['cartId'],
      templatetype: 'CSRLoa',
      csrId: '',
      fullData: this.fullData
    };
    this.fullData['customerInfo'] = {
      phone: this.contactForm.get('phone')?.value,
      email: this.contactForm.get('email')?.value
    };

    this.salesFlow.actionService(obj).subscribe(() => {
      this.showLoaButton = true;
      this.buttonText = 'Resend';
    });
  }

  getLoa() {
    this.isLoading = true;
    this.spinnerText = 'Verifying Loa';
    let obj: any;
    if (this.forms.length > 0) {
      const form = this.forms[this.activeLineIndex];
      const lineKey = this.portingLines[this.activeLineIndex].key;

      obj = {
        cartId: this.fullData?.['info']?.['responseData']?.['createCart']?.['cartId'],
        porting: true,
        phone: this.contactForm.get('phone')?.value,
        email: this.contactForm.get('email')?.value,
        accountNumber: form.get('accountNum')?.value,
        accountName: form.get('accountName')?.value,
        portAddress1: form.get('address')?.value,
        portAddress2: form.get('address2')?.value,
        portCity: form.get('city')?.value,
        portState: form.get('state')?.value,
        portZip: form.get('zip')?.value,
        transferPin: form.get('transferPin')?.value,
        agreementContent: null,
        selectedInfo: this.fullData?.['selectedInfo'],
        lineKey
      };
    } else {
      const si = this.fullData?.['selectedInfo'] || {};
      const inferredKey = si.phonePlan && si.phonePlanOption === 'keep_cci'
        ? 'phonePlan'
        : si.businessPhone && si.businessPhoneOption === 'keep_cci'
          ? 'businessPhone'
          : si.businessPhone2 && si.businessPhone2Option === 'keep_cci'
            ? 'businessPhone2'
            : 'phonePlan';

      obj = {
        cartId: this.fullData?.['info']?.['responseData']?.['createCart']?.['cartId'],
        porting: false,
        phone: this.contactForm.get('phone')?.value,
        email: this.contactForm.get('email')?.value,
        agreementContent: null,
        selectedInfo: this.fullData?.['selectedInfo'],
        lineKey: inferredKey
      };
    }

    this.salesFlow.getAgreement(obj.cartId).subscribe(async (r: any) => {
      if (r.data?.agreementContent) {
        obj.agreementContent = r.data.agreementContent;
        try {
          await this.applySavedAddonPayloads();
        } catch (err) {
          this.isLoading = false;
          this.handleError('Could not update phone details. Please try again.', 'Phone Details');
          return;
        }
        this.salesFlow.agreement(obj).subscribe(() => {
          this.modalOpen = false;
          setTimeout(() => this.handleCarriers({ carriersInfo: obj }), 300);
        });
      } else {
        this.handleError('Customer has not completed LOA', 'LOA');
      }
      this.isLoading = false;
    });
  }

  private async applySavedAddonPayloads(): Promise<void> {
    const saved = Array.isArray(this.fullData?.['savedAddonPayloads']) ? this.fullData?.['savedAddonPayloads'] : [];
    if (!saved.length) return;
    await this.applyAddonPayloads(saved);
  }

  // ==================== COUPON ====================

  // ngOnInitCoupon() {
  //   const all = this.fullData?.['selectedInfo']?.['internetPlan']?.['promotions'] ?? [];
  //   const hasExtenderSelected = (this.fullData?.['selectedInfo']?.['extender'] ?? false);
  //   this.promotions = all.filter((p: any) => {
  //     const classes = p.Classes || [];
  //     const hasBackPocketClass = classes.some((c: any) =>
  //       typeof c.Class === 'string' && c.Class.includes('DSR BackPocket')
  //     );
  //     if (!hasBackPocketClass) return false;
  //     if (!hasExtenderSelected && (p?.isWifiExtenderPromotion ?? false)) return false;
  //     return true;
  //   });

  //   const saved = this.fullData?.['couponInfo'];
  //   if (Array.isArray(saved) && saved.length) {
  //     this.couponForm.get('offers')!.setValue(saved);
  //   }
  // }

  // isOfferSelected(option: any): boolean {
  //   const key = this.promoKey(option);
  //   const selected = this.couponForm.get('offers')!.value || [];
  //   return selected.some((o: any) => this.promoKey(o) === key);
  // }

  // toggleOfferSelection(option: any, checked: boolean) {
  //   const key = this.promoKey(option);
  //   const selected: any[] = [...(this.couponForm.get('offers')!.value || [])];
  //   if (checked && !selected.some(o => this.promoKey(o) === key)) {
  //     selected.push(option);
  //   } else if (!checked) {
  //     const idx = selected.findIndex(o => this.promoKey(o) === key);
  //     if (idx > -1) selected.splice(idx, 1);
  //   }
  //   this.couponForm.get('offers')!.setValue(selected);
  // }

  // private promoKey(p: any): string {
  //   return String(p?.PromotionID ?? p?.LegacyId ?? p?.Name ?? '');
  // }

  // submitCoupon() {
  //   this.isLoading = true;
  //   this.spinnerText = 'Saving your add‑ons…';

  //   const selectedOffers = this.couponForm.get('offers')!.value as any[];
  //   const specialOffers = selectedOffers.map((r: any) => ({
  //     promotionId: r.PromotionID,
  //     promotionLegacyId: r.LegacyID,
  //     promotionName: r.Name,
  //     promotionType: r.Type,
  //     promotionApplyType: r.ApplyType,
  //     discountAmount: r.DiscountAmount,
  //     discountType: r.DiscountCalculation,
  //     startDate: '',
  //     endDate: '',
  //     isTermPromotion: r.IsTermPromotion == 'false' ? false : true,
  //     promotionTerms: [],
  //     displayPriority: r.DisplayPriority,
  //     classes: r.Classes,
  //     promotions: r.Promotions,
  //     isLifelinePromo: false,
  //   }));

  //   const payload = {
  //     cartId: this.fullData?.['responseData']?.createCart?.cartId,
  //     platform_source: 'Web',
  //     obj: { specialOffers }
  //   };

  //   this.salesFlow.patchCartGeneric(payload).subscribe(
  //     (r: any) => {
  //       this.isLoading = false;
  //       if (r?.result === 'success') {
  //         this.handleCoupon({ couponInfo: selectedOffers });
  //       } else {
  //         this.handleError('There was an error saving your selections.', 'Promotions');
  //       }
  //     },
  //     _err => {
  //       this.isLoading = false;
  //       this.handleError('There was an error with your selected offer.', 'Promotions');
  //     }
  //   );
  // }

  // ==================== ADDRESS CHECK (CUSTOMER INFO) ====================

  ngOnInitAddress() {
    const addrType = this.fullData?.['addressInfo']?.['addressType'];
    this.isBusiness = addrType === 'Micro Business' || addrType === 'Business';

    this.customerInfoForm = this.fb.group({
      first: [this.fullData?.['customerInfo']?.['first'], [Validators.required, this.checkForNonNumber]],
      last: [this.fullData?.['customerInfo']?.['last'], [Validators.required, this.checkForNonNumber]],
      phone: [this.fullData?.['customerInfo']?.['phone'], [Validators.required, this.checkForNumber, Validators.minLength(10), this.checkForRepNum]],
      email: [this.getValue('email'), [Validators.required, Validators.email]],
      'text-consent': [false],
      'billingCheckbox': [true],
      'billing-address': [null, Validators.required],
      'billing-address2': [null, Validators.required],
      'billing-city': [null, Validators.required],
      'billing-state': [null, Validators.required],
      'billing-zip': [null, Validators.required],
      honeypot: ['']
    });

    if (this.isBusiness) {
      this.customerInfoForm.addControl(
        'businessName',
        this.fb.control(this.fullData?.['customerInfo']?.['businessName'] || '', Validators.required)
      );
    }

    const existingSameBilling = this.fullData?.['customerInfo']?.['sameBilling'] ?? this.fullData?.['sameBilling'];
    const sameBilling = (existingSameBilling === undefined || existingSameBilling === null) ? true : !!existingSameBilling;
    this.isChecked = sameBilling;
    this.customerInfoForm.controls['billingCheckbox'].setValue(sameBilling, { emitEvent: false });
    this.setBilling({ detail: { checked: sameBilling } });
  }

  getValue(path: string): any {
    const c = this.fullData?.['carriersInfo']?.[path];
    if (c) return c;
    const cu = this.fullData?.['customerInfo']?.[path];
    if (cu) return cu;
    return '';
  }

  setBilling(e: any) {
    const checked = e.detail.checked;
    this.isChecked = checked;

    if (checked) {
      this.customerInfoForm.controls['billing-address'].setValue(this.fullData?.['addressInfo']?.['address1']);
      this.customerInfoForm.controls['billing-city'].setValue(this.fullData?.['addressInfo']?.['city']);
      this.customerInfoForm.controls['billing-state'].setValue(this.fullData?.['addressInfo']?.['state']);
      this.customerInfoForm.controls['billing-zip'].setValue(this.fullData?.['addressInfo']?.['zip']);
      this.customerInfoForm.get('billing-address')?.disable({ emitEvent: false });
      this.customerInfoForm.get('billing-address2')?.disable({ emitEvent: false });
      this.customerInfoForm.get('billing-city')?.disable({ emitEvent: false });
      this.customerInfoForm.get('billing-state')?.disable({ emitEvent: false });
      this.customerInfoForm.get('billing-zip')?.disable({ emitEvent: false });
    } else {
      this.customerInfoForm.get('billing-address')?.enable({ emitEvent: false });
      this.customerInfoForm.get('billing-address2')?.enable({ emitEvent: false });
      this.customerInfoForm.get('billing-city')?.enable({ emitEvent: false });
      this.customerInfoForm.get('billing-state')?.enable({ emitEvent: false });
      this.customerInfoForm.get('billing-zip')?.enable({ emitEvent: false });
      this.customerInfoForm.controls['billing-address'].reset();
      this.customerInfoForm.controls['billing-address2'].reset();
      this.customerInfoForm.controls['billing-city'].reset();
      this.customerInfoForm.controls['billing-state'].reset();
      this.customerInfoForm.controls['billing-zip'].reset();
    }
  }

  checkEmail() {
    if (this.customerInfoForm.invalid) {
      this.customerInfoForm.markAllAsTouched();
      return;
    }
    this.isLoading = true;
    this.spinnerText = 'Checking Cart';
    const cartId = this.getCartId();
    if (!cartId) {
      this.validateEmailAndSubmit();
      return;
    }

    this.salesFlow.getCartInfo(cartId).subscribe((r: any) => {
      const emailVerified = r?.result === 'success' && this.isEmailVerified(r?.data);
      const phoneVerified = r?.result === 'success' && this.isPhoneVerified(r?.data);
      if (emailVerified || phoneVerified) {
        this.fullData['pinFlag'] = true;
        this.submitAddressCheck();
        return;
      }
      this.validateEmailAndSubmit();
    }, error => {
      this.validateEmailAndSubmit();
    });
  }

  private getCartId(): string | null {
    const cartId =
      this.fullData?.['responseData']?.['createCart']?.['cartId'] ||
      this.fullData?.['info']?.['responseData']?.['createCart']?.['cartId'] ||
      this.fullData?.['cartInfo']?.['cartId'] ||
      null;
    return cartId;
  }

  private isEmailVerified(cartInfo: any): boolean {
    return Boolean(
      cartInfo?.emailVerified ??
      cartInfo?.contactInfo?.emailVerified ??
      cartInfo?.primaryAccountHolderInfo?.emailVerified
    );
  }

  private isPhoneVerified(cartInfo: any): boolean {
    return Boolean(
      cartInfo?.phoneVerified ??
      cartInfo?.contactInfo?.phoneVerified ??
      cartInfo?.primaryAccountHolderInfo?.phoneVerified
    );
  }

  private validateEmailAndSubmit(): void {
    const obj = { email: this.customerInfoForm.get('email')?.value };
    this.spinnerText = 'Checking Email';
    this.salesFlow.validateEmail(obj.email).subscribe((r: any) => {
      if (r.result === 'success') {
        if (r.data.message === 'Email is unique') {
          this.submitAddressCheck();
        } else {
          this.handleError(`Failed email check: ${r.data.message}`, 'Email Check');
          this.isLoading = false;
        }
      } else {
        this.handleError('Email check has failed. Please reach out to support.', 'Email Check');
        this.isLoading = false;
      }
    }, _ => { this.isLoading = false; });
  }

  submitAddressCheck() {
    if (this.isChecked) {
      this.createCustomer();
    } else {
      this.spinnerText = 'Validating Billing Address';
      this.isLoading = true;
      const obj = {
        billingAddress1: this.customerInfoForm.get('billing-address')?.value,
        billingAddress2: this.customerInfoForm.get('billing-address2')?.value ? this.customerInfoForm.get('billing-address2')?.value : '',
        billingCity: this.customerInfoForm.get('billing-city')?.value,
        billingState: this.customerInfoForm.get('billing-state')?.value,
        billingZip: this.customerInfoForm.get('billing-zip')?.value,
      };
      this.salesFlow.addressValidation(obj).subscribe((r: any) => {
        if (r.result == 'success' && r.data.addresses[0].address.USPSVALID == 'Y') {
          this.createCustomer();
        } else {
          this.handleError('Please correct the billing address and try again.', 'Billing Address');
          this.isLoading = false;
        }
      }, e => {
        this.handleError('Please correct the billing address and try again.', 'Billing Address');
        this.isLoading = false;
      });
    }
  }

  createCustomer() {
    this.spinnerText = 'Creating Account';
    this.isLoading = true;
    const num = Math.random().toString().slice(2, 12);
    const id = uuidv4();
    const customerId = this.fullData?.['customerInfo']?.['entity-id'] || id;
    const woid = this.fullData?.['customerInfo']?.['work-order-id'] || uuidv4();
    const messageType = this.fullData?.['customerInfo'] ? 'replay-lead' : 'create-customer-web';

    const obj: any = {
      'entity-id': customerId,
      'message-type': messageType,
      businessName: this.customerInfoForm.get('businessName')?.value ? this.customerInfoForm.get('businessName')?.value : undefined,
      first: this.customerInfoForm.get('first')?.value,
      last: this.customerInfoForm.get('last')?.value,
      phone: this.customerInfoForm.get('phone')?.value,
      email: this.customerInfoForm.get('email')?.value,
      company: '',
      address: this.fullData?.['addressInfo']?.['address1'],
      city: this.fullData?.['addressInfo']?.['city'],
      state: this.fullData?.['addressInfo']?.['state'],
      zip: this.fullData?.['addressInfo']?.['zip'],
      'fidium-id': num,
      'text-consent': true,
      billingAddress: {
        address: this.customerInfoForm.get('billing-address')?.value,
        address2: this.customerInfoForm.get('billing-address2')?.value,
        city: this.customerInfoForm.get('billing-city')?.value,
        state: this.customerInfoForm.get('billing-state')?.value,
        zip: this.customerInfoForm.get('billing-zip')?.value
      },
      sameBilling: this.customerInfoForm.get('billingCheckbox')?.value || false,
      'work-order-id': woid,
      origin: 'web'
    };

    // Pass knockvia-user-id (UUID) if we have it, otherwise pass knockvia-user-crm-id
    if (this.userId) {
      obj['knockvia-user-id'] = this.userId;
    } else if (this.userCrmId) {
      obj['knockvia-user-crm-id'] = this.userCrmId;
    }

    const isUpdate = this.fullData?.['customerInfo'] && this.fullData?.['customerInfo']?.['first'];

    // Create in Fidium (required for flow)
    const fidiumCall = this.salesFlow.createOrUpdateCustomer(
      { customerInfo: obj, data: this.fullData },
      isUpdate
    );

    // Create in Marketing Flow database (takerate_marketingflow_dev) - stores as lead/pending
    const marketingFlowPayload = {
      customerInfo: {
        'entity-id': customerId,
        first: obj.first,
        last: obj.last,
        phone: obj.phone,
        email: obj.email,
        address: obj.address,
        address2: '',
        city: obj.city,
        state: obj.state,
        zip: obj.zip,
        'knockvia-user-id': obj['knockvia-user-id'],
        'knockvia-user-crm-id': obj['knockvia-user-crm-id']
      },
      honeyPot: null // Required by marketing flow backend
    };

    const marketingFlowCall = this.salesFlow.createCustomerInMarketingFlow(marketingFlowPayload);

    // Execute both calls - Fidium is required, Marketing Flow is fire-and-forget
    fidiumCall.subscribe(
      (r: any) => {
        if (r.result === 'success') {
          this.fullData['customerInfo'] = { ...obj };
          this.fullData['sameBilling'] = obj.sameBilling;

          // Also create in Marketing Flow (don't block on this)
          marketingFlowCall.subscribe(
            (marketingRes: any) => {
              console.log('Marketing Flow customer created:', marketingRes);
              // Store marketing flow customer ID for reference
              if (marketingRes?.data?.id) {
                this.fullData['marketingFlowCustomerId'] = marketingRes.data.id;
              }
            },
            (marketingErr) => {
              console.error('Error creating in Marketing Flow (non-blocking):', marketingErr);
              // Don't block the flow if Marketing Flow fails
            }
          );

          this.isLoading = false;
          this.handleUpdateCart();
        } else {
          this.isLoading = false;
          this.handleError('Error creating customer', 'Customer');
        }
      },
      (error) => {
        this.isLoading = false;
        this.handleError('Error creating customer', 'Customer');
      }
    );
  }

  handleUpdateCart() {
    this.isLoading = true;
    const partnerName = 'Scout';
    this.fullData['partnerName'] = partnerName;

    this.salesFlow.updateCart(this.fullData).subscribe((res: any) => {
      if (res.result === 'success') {
        this.isLoading = false;
        this.handleAddress({ customerInfo: this.fullData?.['customerInfo'], cartInfo: res.data, pinFlag: this.fullData?.['pinFlag'] });
      } else {
        this.isLoading = false;
        this.handleError('Unable to create cart. Please reach out to support.', 'Cart Error');
      }
    }, e => {
      this.isLoading = false;
      this.handleError('Unable to create cart. Please reach out to support.', 'Cart Error');
    });
  }

  // ==================== PIN PAGE ====================

  sendOTP(x: string) {
    const channel: 'email' | 'sms' = x === 'email' ? 'email' : 'sms';
    if (channel === 'email' && this.otpEmailCooldown > 0) return;
    if (channel === 'sms' && this.otpSmsCooldown > 0) return;

    this.startOtpCooldown(channel);
    this.isLoading = true;
    this.spinnerText = 'Sending verification code...';
    this.mfaChannel = channel;
    this.deliveryMessage = '';

    const payload = {
      email: this.fullData?.['customerInfo']?.['email'],
      phone: this.fullData?.['customerInfo']?.['phone'],
      mfa: channel === 'email' ? 'email' : 'text'
    };

    this.salesFlow.sendOtp(payload).subscribe(
      (res: any) => {
        this.isLoading = false;
        if (res?.result === 'success') {
          this.pinModalOpen = true;
          const { email, phone } = this.fullData?.['customerInfo'];
          this.deliveryMessage = channel === 'email'
            ? `Verification code sent to ${email || 'the customer email on file'}.`
            : `Verification code sent to ${phone || 'the customer phone on file'}.`;
        } else {
          this.handleError(res?.message || 'Unable to send verification code.', 'Verification');
        }
      },
      (err) => {
        this.isLoading = false;
        this.handleError('Unable to send verification code.', 'Verification');
      }
    );
  }

  confirmPin() {
    this.isLoading = true;
    this.spinnerText = 'Verifying your code...';

    const payload = {
      cartId: this.fullData?.['cartInfo']?.['cartId'],
      first: this.fullData?.['customerInfo']?.['first'],
      last: this.fullData?.['customerInfo']?.['last'],
      email: this.fullData?.['customerInfo']?.['email'],
      phone: this.fullData?.['customerInfo']?.['phone'],
      mfa: this.mfaChannel === 'email' ? 'email' : 'text',
      otp: (this.pinInput || '').trim()
    };

    this.salesFlow.verifyOtp(payload).subscribe(
      (res: any) => {
        this.isLoading = false;
        const msg = res?.data?.message || res?.message || '';
        if (msg.toLowerCase().includes('verified')) {
          this.orderNumber = res?.data?.orderDetails?.cart?.orderNumber || this.orderNumber;
          this.pinModalOpen = false;
          this.handlePin({ pinPageInfo: 'success', pinFlag: true });
        } else {
          this.handleError(msg || 'Verification failed. Please try again.', 'Verification');
        }
      },
      (err) => {
        this.isLoading = false;
        this.handleError('Unable to verify code. Please try again.', 'Verification');
      }
    );
  }

  private startOtpCooldown(channel: 'email' | 'sms'): void {
    this.clearOtpCooldown(channel);
    if (channel === 'email') {
      this.otpEmailCooldown = 60;
    } else {
      this.otpSmsCooldown = 60;
    }

    this.otpCooldownTimers[channel] = setInterval(() => {
      if (channel === 'email') {
        this.otpEmailCooldown = Math.max(this.otpEmailCooldown - 1, 0);
        if (this.otpEmailCooldown === 0) {
          this.clearOtpCooldown('email');
        }
      } else {
        this.otpSmsCooldown = Math.max(this.otpSmsCooldown - 1, 0);
        if (this.otpSmsCooldown === 0) {
          this.clearOtpCooldown('sms');
        }
      }
    }, 1000);
  }

  private clearOtpCooldown(channel: 'email' | 'sms'): void {
    const timer = this.otpCooldownTimers[channel];
    if (timer) {
      clearInterval(timer);
      this.otpCooldownTimers[channel] = null;
    }
    if (channel === 'email') {
      this.otpEmailCooldown = 0;
    } else {
      this.otpSmsCooldown = 0;
    }
  }

  // ==================== DIGITAL WALLET ====================

  ngOnInitWallet() {
    this.shouldSkipWallet = !!this.fullData?.['overrideWalletSetup'];
    if (this.shouldSkipWallet) {
      this.handleWallet({ walletPageInfo: null, paymentVerified: true });
      return;
    }

    this.walletForm.patchValue({
      phone: this.fullData?.['customerInfo']?.['phone'],
      email: this.fullData?.['customerInfo']?.['email']
    });
  }

  private startWalletCooldown(channel: 'email' | 'sms'): void {
    this.clearWalletCooldown(channel);
    if (channel === 'email') {
      this.walletEmailCooldown = 60;
    } else {
      this.walletSmsCooldown = 60;
    }

    this.walletCooldownTimers[channel] = setInterval(() => {
      if (channel === 'email') {
        this.walletEmailCooldown = Math.max(this.walletEmailCooldown - 1, 0);
        if (this.walletEmailCooldown === 0) {
          this.clearWalletCooldown('email');
        }
      } else {
        this.walletSmsCooldown = Math.max(this.walletSmsCooldown - 1, 0);
        if (this.walletSmsCooldown === 0) {
          this.clearWalletCooldown('sms');
        }
      }
    }, 1000);
  }

  private clearWalletCooldown(channel: 'email' | 'sms'): void {
    const timer = this.walletCooldownTimers[channel];
    if (timer) {
      clearInterval(timer);
      this.walletCooldownTimers[channel] = null;
    }
    if (channel === 'email') {
      this.walletEmailCooldown = 0;
    } else {
      this.walletSmsCooldown = 0;
    }
  }

  sendPaymentMessage(x: string) {
    const channel: 'email' | 'sms' = x === 'SendEmail' ? 'email' : 'sms';
    if (channel === 'email' && this.walletEmailCooldown > 0) return;
    if (channel === 'sms' && this.walletSmsCooldown > 0) return;

    this.startWalletCooldown(channel);
    this.displayText = '';
    const obj: any = {
      type: x,
      cartId: this.fullData?.['info']?.['responseData']?.['createCart']?.['cartId'],
      templatetype: 'CSRPayment',
      csrId: '',
    };
    obj.fullData = this.fullData;

    this.salesFlow.actionService(obj).subscribe(
      (r: any) => {
        this.showVerifyButton = true;
        const email = this.walletForm.get('email')?.value || this.fullData?.['customerInfo']?.['email'];
        const phone = this.walletForm.get('phone')?.value || this.fullData?.['customerInfo']?.['phone'];
        this.displayText = x === 'SendEmail'
          ? `Email with the payment link sent to ${email || 'the customer email on file'}.`
          : `Text message with the payment link sent to ${phone || 'the customer phone on file'}.`;
      },
      () => {
        this.displayText = '';
      }
    );
  }

  checkPayment() {
    const obj = {
      cartId: this.fullData?.['info']?.['responseData']?.['createCart']?.['cartId'],
    };

    this.salesFlow.getCartInfo(obj.cartId).subscribe((r: any) => {
      if (r.result === 'success' && r.data.paymentusResponse === 'success') {
        this.fullData['paymentVerified'] = true;
        this.handleWallet({ walletPageInfo: 'r.data', paymentVerified: true });
      } else {
        this.handleError('Please finish filling out the payment information.', 'Payment');
        this.fullData['paymentVerified'] = false;
      }
    });
  }

  // ==================== TIME SLOTS ====================

  ngOnInitTime() {
    this.getBookings(new Date());
  }

  private toYMD(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  getBookings(date: any) {
    this.isLoading = true;
    this.spinnerText = 'Getting bookings...';

    const d = new Date(date);
    const formattedDate = this.toYMD(d);
    const endDate = this.addDays(formattedDate, 60);

    const obj = {
      startDate: formattedDate,
      endDate,
      cartId: this.fullData?.['responseData']?.['createCart']?.['cartId']
    };

    if (!this.fullData) this.fullData = {};
    this.fullData['bookingWindow'] = {
      startDate: obj.startDate,
      endDate: obj.endDate
    };

    this.salesFlow.bookingOptions(obj).subscribe((r: any) => {
      if (r.result !== 'success') {
        this.isLoading = false;
        this.handleError('Unable to get available time slots. Please call in to get available times.', 'Time Slots');
        return;
      }

      this.fullData['appointments'] = r.data.appointments || [];
      this.groupedTimeSlots = {};
      this.limitedDates = [];

      const now = new Date();
      now.setSeconds(0, 0);
      const todayMidnight = this.parseLocalYMD(this.toYMD(now));

      for (const appt of this.fullData?.['appointments'] || []) {
        const apptDate = this.parseLocalYMD(appt.date);
        if (apptDate < todayMidnight) continue;

        const validTimeslots = (appt.timeslots || []).filter((t: any) => {
          const when = this.combineLocalDateTime(appt.date, t.scheduleBeginTime);
          return !isNaN(when.getTime()) && when >= now;
        });

        if (!validTimeslots.length) continue;

        if (!this.groupedTimeSlots[appt.date]) this.groupedTimeSlots[appt.date] = [];
        this.groupedTimeSlots[appt.date].push(
          ...validTimeslots.map((t: any) => ({
            ...t,
            scheduleDateOfInstallation: appt.date,
            label: `${t.scheduleBeginTime} - ${t.scheduleEndTime}`
          }))
        );
      }

      for (const date of Object.keys(this.groupedTimeSlots)) {
        this.groupedTimeSlots[date].sort((a: any, b: any) => {
          const aTime = this.combineLocalDateTime(date, a.scheduleBeginTime).getTime();
          const bTime = this.combineLocalDateTime(date, b.scheduleBeginTime).getTime();
          return aTime - bTime;
        });
      }

      this.limitedDates = Object.keys(this.groupedTimeSlots).sort();
      this.availableDates = new Set(this.limitedDates);

      if (!this.selectedDate || !this.availableDates.has(this.selectedDate)) {
        this.selectedDate = this.limitedDates[0] || null;
        this.selectedTimeSlot = null;
      }

      this.isLoading = false;
    });
  }

  parseLocalYMD(ymd: string): Date {
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, (m as number) - 1, d as number, 0, 0, 0, 0);
  }

  private combineLocalDateTime(ymd: string, time: string): Date {
    const [y, m, d] = ymd.split('-').map(Number);
    const norm = time.replace(/^(\d{1,2}:\d{2})(am|pm)$/i, '$1 $2');
    const parts = norm.trim().split(/\s+/);
    const hm = parts[0];
    const mer = parts[1]?.toLowerCase();
    const [hhStr, mmStr] = hm.split(':');
    let hh = Number(hhStr);
    const mm = Number(mmStr || '0');
    if (mer === 'am' || mer === 'pm') {
      hh = hh % 12 + (mer === 'pm' ? 12 : 0);
    }
    return new Date(y, (m as number) - 1, d as number, hh, mm, 0, 0);
  }

  private addDays(ymd: string, days: number): string {
    const base = this.parseLocalYMD(ymd);
    base.setDate(base.getDate() + days);
    return this.toYMD(base);
  }

  selectTimeSlot(slot: any) {
    this.selectedTimeSlot = slot;
    this.handleTime({
      selectedTime: {
        selectedDate: slot.scheduleDateOfInstallation,
        timeslot: slot.timeSlot,
        resourceId: slot.resourceId,
        cartId: this.fullData?.['responseData']?.['createCart']?.['cartId'],
        appointmentWindowId: null,
        startTime: slot.scheduleBeginTime,
        endTime: slot.scheduleEndTime
      }
    });
  }

  // ==================== REVIEW ====================

  ngOnInitReview() {
    this.getInfo();
  }

  getInfo() {
    const cartId = this.fullData?.['info']?.['responseData']?.['createCart']?.['cartId'];
    this.salesFlow.getCartInfo(cartId).subscribe((response: any) => {
      if (response.result === 'success') {
        this.cartInfo = response.data;
        this.buildRows();
      } else {
        this.handleError('Failed to retrieve cart information.', 'Cart Info');
      }
    }, error => {
      this.handleError('Error fetching cart info', error);
    });
  }

  private buildRows(): void {
    const selected = this.cartInfo;
    if (!selected) return;

    this.charges = [];
    this.offers = [];

    if (selected.selectedPlan?.planPrice > 0) {
      let planLabel = this.removeStars(selected.selectedPlan.planName);
      this.charges.push({
        label: planLabel,
        amount: Number(selected.selectedPlan.planPrice)
      });
    }

    this.charges.push(
      ...selected.addOnDetails.internetAddons.map((addon: any) => {
        const autoPromo = addon.promotions?.find(
          (promo: any) =>
            Number(promo.discountAmount || promo.DiscountAmount || 0) >= Number(addon.addOnPrice)
        );
        const finalPrice: number | 'Included' = autoPromo ? 'Included' : Number(addon.addOnPrice);
        return {
          label: this.removeStars(addon.addOnName),
          amount: finalPrice
        };
      })
    );

    if (selected.addOnDetails.oneTimeChargesAddons) {
      this.charges.push(
        ...selected.addOnDetails.oneTimeChargesAddons.map((addon: any) => {
          const autoPromo = addon.promotions?.find(
            (promo: any) =>
              Number(promo.discountAmount || promo.DiscountAmount || 0) >= Number(addon.addOnPrice)
          );
          const finalPrice: number | 'Included' = autoPromo ? 'Included' : Number(addon.addOnPrice);
          return {
            label: this.removeStars(addon.addOnName),
            amount: finalPrice
          };
        })
      );
    }

    if (selected.addOnDetails.phoneAddons) {
      this.charges.push(
        ...selected.addOnDetails.phoneAddons.map((addon: any) => {
          const autoPromo = addon.promotions?.find(
            (promo: any) =>
              Number(promo.discountAmount || promo.DiscountAmount || 0) >= Number(addon.addOnPrice)
          );
          const finalPrice: number | 'Included' = autoPromo ? 'Included' : Number(addon.addOnPrice);
          return {
            label: this.removeStars(addon.addOnName),
            amount: finalPrice
          };
        })
      );
    }

    this.totalCharges = this.charges.reduce((sum, row) => {
      if (typeof row.amount === 'number') {
        return sum + (row.amount as number);
      }
      return sum;
    }, 0);

    const allPlanPromotions = Array.isArray(selected.selectedPlan?.promotions)
      ? selected.selectedPlan.promotions
      : [];

    this.discounts = allPlanPromotions
      .map((promo: any) => {
        const label = promo.promotionName || promo.Name;
        let amount = 0;
        if (promo.isTermPromotion && Array.isArray(promo.promotionTerms) && promo.promotionTerms.length) {
          amount = Number(promo.promotionTerms[0].DiscountAmount || 0);
        } else if (Array.isArray(promo.Term) && promo.Term.length) {
          amount = Number(promo.Term[0].DiscountAmount || 0);
        } else {
          amount = Number(promo.discountAmount || promo.DiscountAmount || 0);
        }
        return { label, amount };
      })
      .filter((promo: any) => promo.amount > 0);

    this.totalDiscounts = this.discounts.reduce((sum, row) => {
      const amount = typeof row.amount === 'number' ? row.amount : 0;
      return sum + amount;
    }, 0);

    const cartMonthly = Number(selected?.monthlyCharges);
    this.finalPrice = !Number.isNaN(cartMonthly)
      ? cartMonthly
      : this.totalCharges - this.totalDiscounts;
  }

  removeStars(incoming: string): string {
    return incoming.replace(/\*\*_/g, '');
  }

  async submitReview() {
    if (this.isLoading) return;

    const cartId = this.fullData?.['responseData']?.['createCart']?.['cartId'];
    const workOrderId = this.fullData?.['customerInfo']?.['work-order-id'];
    const existingFlow = this.isExistingServiceFlow();
    this.isExistingFlow = existingFlow;

    if (!cartId || !workOrderId) {
      this.handleError('Missing cart information. Please restart the flow.', 'Order');
      return;
    }

    try {
      if (!existingFlow && this.fullData?.['selectedTime']) {
        this.isLoading = true;
        this.spinnerText = 'Confirming appointment...';
        const booked = await this.confirmAndBookAppointment(cartId);
        if (!booked) {
          this.isLoading = false;
          return;
        } else {
          const appointmentPayload = {
            selectedDate: this.fullData?.['selectedTime']?.['selectedDate'],
            timeslot: this.fullData?.['selectedTime']?.['timeslot'],
            resourceId: this.fullData?.['selectedTime']?.['resourceId'],
            cartId: this.cartInfo?.['cartId'] || cartId,
            appointmentWindowId: this.fullData?.['selectedTime']?.['appointmentWindowId'] ?? null,
            startTime: this.fullData?.['selectedTime']?.['startTime'],
            endTime: this.fullData?.['selectedTime']?.['endTime']
          };
          const bookResult: any = await firstValueFrom(
            this.salesFlow.bookAppointment(appointmentPayload)
          );
          if (bookResult?.result !== 'success') {
            this.isLoading = false;
            this.handleError('We could not confirm the appointment. Please try again or choose another slot.', 'Appointment');
            return;
          }
        }
      }

      this.isLoading = true;
      this.spinnerText = existingFlow ? 'Submitting as existing...' : 'Submitting order...';

      if (existingFlow) {
        const existingResult = await firstValueFrom(
          this.salesFlow.submitExistingService(cartId)
        );
        if (existingResult.result === 'success') {
          this.isModalOpen = true;
        }
        this.isLoading = false;
        return;
      }

      // Submit the order first
      this.spinnerText = 'Submitting order...';
      const payload = { cartId, workOrderId, data: this.fullData };
      const response: any = await firstValueFrom(
        this.salesFlow.submitOrder(payload)
      );

      if (response.result === 'success') {
        // After successful order submission, create customer and work order in main database
        this.spinnerText = 'Creating customer record...';
        const customerPayload = {
          customerInfo: {
            first: this.fullData['customerInfo'].first,
            last: this.fullData['customerInfo'].last,
            phone: this.fullData['customerInfo'].phone,
            email: this.fullData['customerInfo'].email,
            address: this.fullData['customerInfo'].address,
            city: this.fullData['customerInfo'].city,
            state: this.fullData['customerInfo'].state,
            zip: this.fullData['customerInfo'].zip,
            'work-order-id': workOrderId,
            company_name: '',
            'knockvia-user-id': this.userId,
            'knockvia-user-crm-id': this.userCrmId
          }
        };

        // Create customer and work order in main database (KnockVia)
        try {
          const customerResult: any = await firstValueFrom(
            this.salesFlow.createCustomerAndWorkOrderInKnockVia(customerPayload)
          );
          if (customerResult?.data) {
            this.fullData['knockViaCustomerId'] = customerResult.data.customer_id;
            this.fullData['knockViaWorkOrderId'] = customerResult.data.work_order_id;
            console.log('Customer and work order created in main database:', customerResult.data);
          }
        } catch (customerError) {
          console.error('Error creating customer in main database (non-blocking):', customerError);
          // Don't block success message if customer creation fails
        }

        this.orderNumber = response.data.orderDetails.cart.orderNumber;
        this.isModalOpen = true;
      } else {
        this.handleError('Failed to submit order. Please reach out to support.', 'Order');
      }
      this.isLoading = false;
    } catch (error) {
      console.error('Error submitting order', error);
      this.isLoading = false;
      this.handleError('Something went wrong while submitting the order. Please try again or reach out to support.', 'Order');
    }
  }

  // ==================== HELPER METHODS ====================

  private fetchPartnerAccessMetadata(): void {
    if (this.partnerMetadataLoaded) return;

    // For customer-facing flows, skip partner metadata fetch
    // Set defaults instead
    this.fullData['overrideWalletSetup'] = false;
    this.fullData['allowPaymentScreen'] = true;
    this.partnerMetadataLoaded = true;

    // Optionally, you could make an unauthenticated call if the backend supports it
    // this.salesFlow.getPartnerAccess().subscribe(...)
  }

  private shouldSkipWalletSetup(): boolean {
    return !!this.fullData?.['overrideWalletSetup'];
  }

  private initPlaceAutocomplete(): void {
    const googleMaps = (window as any).google;
    if (!this.addressInput || !googleMaps?.maps?.places) return;

    const autocomplete = new googleMaps.maps.places.Autocomplete(this.addressInput.nativeElement, {
      types: ['address'],
      componentRestrictions: { country: 'us' },
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place?.address_components) {
        const addressComponents = this.parseAddressComponents(place.address_components);
        this.addressForm.patchValue({
          address: place.formatted_address,
          city: addressComponents.city,
          state: addressComponents.state,
          zip: addressComponents.zipCode,
        });
      }
    });
  }

  private parseAddressComponents(components: any[]): any {
    let streetNumber = '';
    let route = '';
    let city = '';
    let state = '';
    let zipCode = '';

    for (const component of components) {
      const componentType = component.types[0];
      switch (componentType) {
        case 'street_number':
          streetNumber = component.long_name;
          break;
        case 'route':
          route = component.long_name;
          break;
        case 'locality':
          city = component.long_name;
          break;
        case 'administrative_area_level_1':
          state = component.short_name;
          break;
        case 'postal_code':
          zipCode = component.long_name;
          break;
      }
    }

    return {
      streetAddress: `${streetNumber} ${route}`.trim(),
      city,
      state,
      zipCode,
    };
  }

  private checkForNumber(control: AbstractControl): ValidationErrors | null {
    const numbers = /^[0-9-]*$/;
    return control.value && !control.value.match(numbers) ? { number: true } : null;
  }

  private isZipValid(control: AbstractControl): ValidationErrors | null {
    const value = control.value || '';
    if (!value) return null;
    return value.length === 5 || value.length === 10 ? null : { zipCodeInvalid: true };
  }

  private checkForNonNumber(control: AbstractControl): ValidationErrors | null {
    const letters = /^[a-zA-Z\s\.'-]*$/;
    if (control.value && !control.value.match(letters)) {
      return { hasNumbers: true };
    }
    return null;
  }

  private checkForRepNum(control: AbstractControl): ValidationErrors | null {
    const currentUserNum = '';
    if (!currentUserNum) return null;
    if (control.value && control.value === currentUserNum) {
      return { currentUserNum: true };
    }
    return null;
  }

  private setLoadingState(loading: boolean, verbage: string = ''): void {
    this.isLoading = loading;
    this.spinnerText = verbage;
  }

  private zip5(v: string): string {
    return (v || '').replace(/\D/g, '').slice(0, 5);
  }

  handleError(customMessage?: string, title?: string) {
    this.isLoading = false;
    this.error = true;
    this.errorText = customMessage || 'We encountered an issue while processing your request. Please try again later.';
    console.error(`[SalesFlow] Error${title ? ` (${title})` : ''}:`, this.errorText);
  }

  dismissError() {
    this.error = false;
    this.errorText = '';
  }

  private async confirmAndBookAppointment(cartId: string): Promise<boolean> {
    const selection = this.fullData?.['selectedTime'];
    if (!selection) return true;

    try {
      const bookPayload = {
        selectedDate: selection.selectedDate,
        timeslot: selection.timeslot ?? selection['timeSlot'],
        resourceId: selection.resourceId,
        cartId,
        appointmentWindowId: selection['appointmentWindowId'] ?? null,
        startTime: selection['startTime'],
        endTime: selection['endTime']
      };

      const bookingResponse: any = await firstValueFrom(
        this.salesFlow.bookingOptions({
          cartId,
          startDate: selection.selectedDate,
          selectedDate: selection.selectedDate
        })
      );

      if (bookingResponse.result !== 'success') {
        this.timeUnavailable();
        return false;
      }

      const slotStillAvailable = (
        bookingResponse?.data?.appointments?.length > 0 &&
        bookingResponse?.data?.appointments[0].timeslots.find(
          (r: any) => r.timeSlot === bookPayload.timeslot
        )
      );

      if (!slotStillAvailable) {
        this.timeUnavailable();
        return false;
      }

      this.fullData['appointmentResponse'] = bookingResponse.data;
      return true;
    } catch (error) {
      console.error('Error booking appointment', error);
      this.timeUnavailable();
      return false;
    }
  }

  private isExistingServiceFlow(): boolean {
    const explicitOverride = !!this.fullData?.['existingServiceOverride'];
    const flags = this.fullData?.['responseData']?.['existingFlags'] || {};
    const hasExisting = !!(flags['hasExistingOrder'] || flags['hasExistingService']);
    return explicitOverride || hasExisting;
  }

  async timeUnavailable() {
    this.handleError('Time slot is no longer available, please choose another one.', 'Appointment');
    this.handleReview('timeUnavailable');
  }

  // Modal control methods
  modalControl(e: string) {
    if (e === 'open') {
      this.isModalOpen = true;
    } else {
      this.isModalOpen = false;
      this.resetFlow();
    }
  }

  closePreOrderModal() {
    this.preOrderModalOpen = false;
    this.resetToForm();
  }

  resetToForm() {
    this.showAddressList = false;
    this.showForm = true;
    this.hideButton = true;
    this.selectedAddressIndex = null;
    this.selectedAddressObj = null;
    this.selectedAddress = null;
    this.eligibleAddresses = [];
    this.showPreOrderIframe = false;
    this.preOrderUrl = null;
    this.preOrderModalOpen = false;
    this.existingServiceData = null;
    this.existingServiceMessage = '';
    this.suggestions = [];
  }

  // Address modal methods (for compatibility with existing template)
  addressModal(status: string) {
    if (this.isHoneypotTriggered(this.addressForm)) {
      return;
    }

    this.showLoading(async () => {
      this.spinnerText = status === 'open' ? "Let's check your address!" : 'Loading!';
      this.eligibleAddresses = [];
      const addr = this.addressForm.value.address;

      try {
        const res: any = await firstValueFrom(
          this.salesFlow.locationLookup(addr)
        );
        if (res?.result === 'success' && Array.isArray(res.data) && res.data.length) {
          this.eligibleAddresses = res.data;
          this.addressCheckModal = status === 'open';
        } else {
          this.isLoading = false;
          this.handleError(res?.message || 'Unable to find this address. Please try again.');
        }
      } catch (err) {
        console.error('Error posting address:', err);
        this.isLoading = false;
        this.handleError();
      }
    });
  }

  private buildServiceabilityPayload(address: any) {
    const locationType = this.addressForm.value.locationType || 'Residential';
    return {
      zip: address?.zip || this.addressForm.value.zip,
      city: address?.city || this.addressForm.value.city,
      address1: `${address?.streetNumber || ''} ${address?.street || ''}`.trim() || this.addressForm.value.streetAddress,
      address2: address?.unit || '',
      state: address?.state || this.addressForm.value.state,
      street: address?.street,
      streetNumber: address?.streetNumber,
      endUserLocation: address?.endUserLocation,
      addressId: address?.addressId,
      unitId: address?.endUserLocation,
      fullAddress: address?.address || this.addressForm.value.address,
      existing: this.addressForm.value.existing,
      salesChannel: 'DSR',
      platform_source: 'Web',
      addressType: locationType === 'Business' ? 'Business' : 'Residential',
      locationType
    };
  }

  private extractPlans(data: any) {
    const catalog = data?.catalog || data?.responseData?.catalog || [];
    return catalog.map((entry: any) => {
      const internet = entry?.internet || entry;
      const price = parseFloat(internet?.productPrice ?? internet?.recurringAmount ?? internet?.price ?? 0);
      const name = internet?.productName || internet?.PlanName || internet?.planName;
      return {
        ...internet,
        PlanName: name,
        ServicePlanName: name,
        Description: internet?.productDescription || internet?.Description,
        recurringAmount: isNaN(price) ? 0 : price
      };
    });
  }

  private isHoneypotTriggered(formGroup: FormGroup): boolean {
    const honeypotValue = formGroup.get('honeypot')?.value;
    if (honeypotValue) {
      console.warn('Honeypot triggered. Potential fraud detected.');
      this.handleError('Suspicious activity detected. Please try again later.');
      return true;
    }
    return false;
  }

  private showLoading(action: () => Promise<any>, minTime: number = 500): Promise<void> {
    this.isLoading = true;
    const start = Date.now();

    return action().finally(() => {
      const elapsed = Date.now() - start;
      const remainingTime = minTime - elapsed;

      setTimeout(() => {
        this.isLoading = false;
      }, Math.max(remainingTime, 0));
    });
  }

  // Pin modal methods
  pinModal(status: string) {
    this.pinModalOpen = status === 'open' ? true : false;
  }

  // Info modal methods
  infoModal(status: string) {
    this.infoModalOpen = status === 'open' ? true : false;
  }

  // Format phone number helper
  formatPhoneNumber(phoneNumber: string): string {
    if (!phoneNumber) return '';
    const cleaned = phoneNumber.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    return match ? `(${match[1]}) ${match[2]}-${match[3]}` : phoneNumber;
  }

  // Select plan method (for compatibility)
  selectPlan(plan: any) {
    this.selectedInternet = plan;
    this.selectedPlan = plan;
    this.setCheckBoxValInternet(plan);
    this.setStep('plans');
  }

  // Phone number option change handler
  onPhoneNumberOptionChange(val: 'new' | 'keep_cci' | 'port_other' | null): void {
    this.phoneNumberOption = val;
    this.updateHideButton();
  }

  // Business line option change handlers
  onBusinessLine1OptionChange(v: 'new' | 'consolidated' | 'other' | null) {
    this.businessLine1Flag = v;
    this.updateHideButton();
  }

  onBusinessLine2OptionChange(v: 'new' | 'consolidated' | 'other' | null) {
    this.businessLine2Flag = v;
    this.updateHideButton();
  }

  resetFlow() {
    // Reset step to beginning
    this.currentStep = 'leadinfo';
    this.stepHistory = [];
    this.progressPercentage = 0;

    // Reset fullData but keep userId/userCrmId if they came from URL
    const savedUserId = this.userId;
    const savedUserCrmId = this.userCrmId;
    this.fullData = {};
    if (savedUserId) {
      this.fullData['userId'] = savedUserId;
      this.userId = savedUserId;
    }
    if (savedUserCrmId) {
      this.fullData['userCrmId'] = savedUserCrmId;
      this.userCrmId = savedUserCrmId;
    }

    // Reset forms
    this.addressForm.reset({
      locationType: 'Residential',
      existing: false,
      honeypot: ''
    });
    this.customerInfoForm.reset({
      'text-consent': false,
      'billingCheckbox': true,
      honeypot: ''
    });
    this.walletForm.reset();
    this.carriersForm.reset();
    this.contactForm.reset();
    this.forms = [];
    this.activeLineIndex = 0;

    // Reset plan selection
    this.selectedInternet = null;
    this.selectedPlanIndex = null;
    this.selectedPlan = null;

    // Reset address
    this.showAddressList = false;
    this.selectedAddress = null;

    // Reset carriers
    this.portingLines = [];
    this.showPhone = false;
    this.hideButton = true;
    this.showLoaButton = false;
    this.buttonText = 'Send';
    this.loaRequired = false;
    this.needsLoaOnly = false;
    this.hasDuplicatePortNumbers = false;
    this.allowedPortAreaCodes = [];
    this.modalOpen = false;

    // Reset PIN
    this.pinInput = '';
    this.pinCount = 0;
    this.pinModalOpen = false;
    this.deliveryMessage = '';
    this.clearOtpCooldown('email');
    this.clearOtpCooldown('sms');

    // Reset Wallet
    this.paymentVerified = false;
    this.walletModalOpen = false;
    this.displayText = '';
    this.showVerifyButton = false;
    this.shouldSkipWallet = false;
    this.clearWalletCooldown('email');
    this.clearWalletCooldown('sms');

    // Reset Time Slots
    this.timeSlots = [];
    this.groupedTimeSlots = {};
    this.selectedTimeSlot = null;

    // Reset Review
    this.charges = [];
    this.discounts = [];
    this.offers = [];
    this.totalCharges = 0;
    this.totalDiscounts = 0;
    this.finalPrice = 0;

    // Reset modals and flags
    this.isModalOpen = false;
    this.isExistingFlow = false;
    this.preOrderModalOpen = false;
    this.addressCheckModal = false;
    this.infoModalOpen = false;
    this.error = false;
    this.errorText = '';

    // Reset order info
    this.orderNumber = '';
    this.cartInfo = null;

    // Reset loading states
    this.isLoading = false;
    this.spinnerText = '';

    // Reset business flag
    this.isBusiness = false;
    this.sameBilling = true;
    this.isChecked = true;

    // Update progress
    this.updateProgress();
  }
}